"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChefHat, ConciergeBell, Shield } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import SectionContainer from "@/components/common/SectionContainer";
const policiesData = [{
  id: "vendor-policy",
  iconName: "chef-hat",
  title: "Open Vendor Policy",
  description: "Bring any licensed & insured vendors of your choice with zero penalty fees."
}, {
  id: "staffing-policy",
  iconName: "concierge-bell",
  title: "Mandatory Staffing",
  description: "$450 flat rate. Venue staff member manages building access, safety, and venue guidance."
}, {
  id: "alcohol-security-policy",
  iconName: "shield",
  title: "Alcohol & Security",
  description: "$400 BYOB flat fee + TABC bartender required. Off-duty Grapevine police officer ($65/hr) required."
}];
export default function Policies() {
  const [activeTab, setActiveTab] = useState<'elopement' | 'micro' | 'full'>('micro');
  const shouldReduceMotion = useReducedMotion();
  useEffect(() => {
    const handleTabChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        tab: 'elopement' | 'micro' | 'full';
      }>;
      if (customEvent.detail?.tab) {
        setActiveTab(customEvent.detail.tab);
      }
    };
    window.addEventListener('pricing-tab-change', handleTabChange);
    return () => {
      window.removeEventListener('pricing-tab-change', handleTabChange);
    };
  }, []);
  if (activeTab === 'elopement') return null;
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "chef-hat":
        return <ChefHat className="size-6 text-foreground stroke-[1.5]" aria-hidden="true" />;
      case "concierge-bell":
        return <ConciergeBell className="size-6 text-foreground stroke-[1.5]" aria-hidden="true" />;
      case "shield":
        return <Shield className="size-6 text-foreground stroke-[1.5]" aria-hidden="true" />;
      default:
        return null;
    }
  };
  return <section id="venuepolicies" data-nav="light" className="bg-background text-foreground pt-8 md:pt-10 pb-8 md:pb-12 font-default">
      <SectionContainer>
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-center max-w-2xl mx-auto mb-16 md:mb-20">
          <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3 block">
            Guidelines & Requirements
          </span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Venue policies
          </h2>
          <div className="w-12 h-px bg-border mx-auto mt-6" aria-hidden="true" />
        </motion.div>

        {/* Policies Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {policiesData.map((policy, index) => <motion.div key={policy.id} data-index={index} variants={itemVariants} className="h-full flex">
              <Card className="flex flex-col flex-1 text-center bg-card text-card-foreground border border-border rounded-lg shadow-xs p-6 md:p-8 justify-between hover:border-foreground/30 transition-[border-color,box-shadow] duration-300">
                <CardHeader className="p-0 flex flex-col items-center gap-4">
                  <div className="size-14 rounded-lg border border-border bg-background flex items-center justify-center shadow-2xs">
                    {renderIcon(policy.iconName)}
                  </div>
                  <CardTitle className="text-xl font-bold text-card-foreground tracking-tight mt-2">
                    {policy.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 pt-4 mt-auto">
                  <p className="text-sm md:text-base text-card-foreground/80 leading-relaxed">
                    {policy.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>
      </SectionContainer>
    </section>;
}